import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { useMemo } from 'react';

type GridRulerProps = {
  zoom: number;
  panOffset: { x: number; y: number };
  containerWidth: number;
  containerHeight: number;
};

const RULER_THICKNESS = 24;
const MINOR_TICK_HEIGHT = 6;
const MAJOR_TICK_HEIGHT = 12;
const LABEL_OFFSET = 2;

const getTickInterval = (pixelsPerFoot: number): number => {
  if (pixelsPerFoot >= 20) return 1;
  if (pixelsPerFoot >= 10) return 2;
  if (pixelsPerFoot >= 4) return 5;
  return 10;
};

const getMajorTickMultiple = (tickInterval: number): number => {
  if (tickInterval >= 10) return tickInterval;
  if (tickInterval >= 5) return 10;
  return 5;
};

export default function GridRuler({
  zoom,
  panOffset,
  containerWidth,
  containerHeight
}: Readonly<GridRulerProps>) {
  const pixelsPerFoot = STRUCTURE_GRID_SIZE * zoom;
  const tickInterval = getTickInterval(pixelsPerFoot);
  const majorMultiple = getMajorTickMultiple(tickInterval);

  const horizontalTicks = useMemo(() => {
    const ticks: { x: number; feet: number; isMajor: boolean }[] = [];
    const offsetPx = panOffset.x;
    const startFeet =
      Math.floor(-offsetPx / pixelsPerFoot / tickInterval) * tickInterval;
    const endFeet =
      Math.ceil((containerWidth - offsetPx) / pixelsPerFoot / tickInterval) *
      tickInterval;

    for (let ft = startFeet; ft <= endFeet; ft += tickInterval) {
      const x = ft * pixelsPerFoot + offsetPx;
      if (x >= RULER_THICKNESS && x <= containerWidth) {
        ticks.push({ x, feet: ft, isMajor: ft % majorMultiple === 0 });
      }
    }
    return ticks;
  }, [panOffset.x, pixelsPerFoot, tickInterval, majorMultiple, containerWidth]);

  const verticalTicks = useMemo(() => {
    const ticks: { y: number; feet: number; isMajor: boolean }[] = [];
    const offsetPx = panOffset.y;
    const startFeet =
      Math.floor(-offsetPx / pixelsPerFoot / tickInterval) * tickInterval;
    const endFeet =
      Math.ceil((containerHeight - offsetPx) / pixelsPerFoot / tickInterval) *
      tickInterval;

    for (let ft = startFeet; ft <= endFeet; ft += tickInterval) {
      const y = ft * pixelsPerFoot + offsetPx;
      if (y >= RULER_THICKNESS && y <= containerHeight) {
        ticks.push({ y, feet: ft, isMajor: ft % majorMultiple === 0 });
      }
    }
    return ticks;
  }, [
    panOffset.y,
    pixelsPerFoot,
    tickInterval,
    majorMultiple,
    containerHeight
  ]);

  return (
    <>
      <div
        className='pointer-events-none absolute left-0 top-0 z-10'
        style={{
          width: containerWidth,
          height: RULER_THICKNESS,
          backgroundColor: 'rgba(255, 255, 255, 0.9)'
        }}
      >
        <div className='absolute bottom-0 left-0 h-px w-full bg-gray-300' />
        {horizontalTicks.map((tick) => (
          <div key={`h-${tick.feet}`} className='absolute bottom-0'>
            <div
              className='absolute bottom-0 bg-gray-400'
              style={{
                left: tick.x,
                width: 1,
                height: tick.isMajor ? MAJOR_TICK_HEIGHT : MINOR_TICK_HEIGHT
              }}
            />
            {tick.isMajor && (
              <span
                className='absolute select-none text-gray-500'
                style={{
                  left: tick.x + LABEL_OFFSET,
                  top: LABEL_OFFSET,
                  fontSize: 9,
                  lineHeight: '10px'
                }}
              >
                {tick.feet}ft
              </span>
            )}
          </div>
        ))}
      </div>

      <div
        className='pointer-events-none absolute left-0 top-0 z-10'
        style={{
          width: RULER_THICKNESS,
          height: containerHeight,
          backgroundColor: 'rgba(255, 255, 255, 0.9)'
        }}
      >
        <div className='absolute right-0 top-0 h-full w-px bg-gray-300' />
        {verticalTicks.map((tick) => (
          <div key={`v-${tick.feet}`} className='absolute right-0'>
            <div
              className='absolute right-0 bg-gray-400'
              style={{
                top: tick.y,
                height: 1,
                width: tick.isMajor ? MAJOR_TICK_HEIGHT : MINOR_TICK_HEIGHT
              }}
            />
            {tick.isMajor && (
              <span
                className='absolute select-none text-gray-500'
                style={{
                  top: tick.y + LABEL_OFFSET,
                  left: LABEL_OFFSET,
                  fontSize: 9,
                  lineHeight: '10px'
                }}
              >
                {tick.feet}ft
              </span>
            )}
          </div>
        ))}
      </div>

      <div
        className='pointer-events-none absolute left-0 top-0 z-10 border-b border-r border-gray-300'
        style={{
          width: RULER_THICKNESS,
          height: RULER_THICKNESS,
          backgroundColor: 'rgba(255, 255, 255, 0.95)'
        }}
      >
        <span
          className='absolute select-none text-gray-400'
          style={{
            fontSize: 7,
            bottom: 2,
            right: 3
          }}
        >
          ft
        </span>
      </div>
    </>
  );
}
